import React, { useEffect, useRef, useState } from "react";

const Ticker = ({ text = "Ticker Text", speed = 0.8, className = "" }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const animationRef = useRef(null);
  const frameCountRef = useRef(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Detect if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      setIsMobile(mobileRegex.test(userAgent));
    };

    checkIfMobile();
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (!containerRef.current || !textRef.current || !isLoaded) return;

    const container = containerRef.current;
    const textElement = textRef.current;
    
    // Clear any existing clones
    while (container.children.length > 1) {
      container.removeChild(container.lastChild);
    }
    
    // For mobile, create clones for continuous flow
    if (isMobile) {
      // Set initial position outside the visible area (to the right)
      let position = container.offsetWidth;
      textElement.style.transform = `translateX(${position}px)`;
      
      const numClones = 2;
      const clones = [];
      
      for (let i = 0; i < numClones; i++) {
        const clone = textElement.cloneNode(true);
        clone.style.position = 'absolute';
        container.appendChild(clone);
        clones.push(clone);
      }
      
      // Calculate gap between repeated texts
      const gap = 50;
      
      // Position the clones right after each other
      clones.forEach((clone, index) => {
        clone.style.transform = `translateX(${position + (textElement.offsetWidth + gap) * (index + 1)}px)`;
      });

      // Animation function with throttling for mobile
      const animate = () => {
        frameCountRef.current += 1;
        const frameSkip = 2;
        
        if (frameCountRef.current >= frameSkip) {
          position -= speed;
          frameCountRef.current = 0;
        }

        // If original text is too far to the left, reset position
        if (position < -textElement.offsetWidth - gap) {
          position += textElement.offsetWidth + gap;
        }

        textElement.style.transform = `translateX(${position}px)`;
        
        // Update all clones
        clones.forEach((clone, index) => {
          clone.style.transform = `translateX(${position + (textElement.offsetWidth + gap) * (index + 1)}px)`;
        });
        
        animationRef.current = requestAnimationFrame(animate);
      };

      animationRef.current = requestAnimationFrame(animate);
    } else {
      // For desktop, use a different approach with CSS animation
      // This ensures the text smoothly reaches the left end before resetting
      
      // First, set up the text element
      textElement.style.position = 'absolute';
      textElement.style.left = '100%'; // Start from the right edge
      textElement.style.transform = 'none'; // Remove any transform
      
      // Create a CSS animation with a smooth transition
      const keyframes = `
        @keyframes tickerDesktop {
          0% {
            left: 100%;
          }
          100% {
            left: -100%;
          }
        }
      `;
      
      // Add the keyframes to the document
      const style = document.createElement('style');
      style.innerHTML = keyframes;
      document.head.appendChild(style);
      
      // Apply the animation to the text element with a slow movement (30s)
      // and a smooth transition between cycles
      textElement.style.animation = `tickerDesktop 30s linear infinite`;
      
      // Add a second text element for a smoother transition
      const secondText = textElement.cloneNode(true);
      secondText.style.position = 'absolute';
      secondText.style.left = '100%';
      secondText.style.animation = `tickerDesktop 30s linear infinite`;
      secondText.style.animationDelay = '15s'; // Start halfway through the first animation
      container.appendChild(secondText);
      
      // Clean up function
      return () => {
        if (style.parentNode) {
          style.parentNode.removeChild(style);
        }
        if (container.contains(secondText)) {
          container.removeChild(secondText);
        }
      };
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Clean up clones
      while (container.children.length > 1) {
        container.removeChild(container.lastChild);
      }
    };
  }, [text, speed, isMobile, isLoaded]);

  // Show once loaded
  const visibility = !isLoaded ? "invisible" : "visible";

  return (
    <div
      ref={containerRef}
      className={`w-full overflow-hidden flex justify-center items-center relative ${className}`}
      style={{ visibility }}
    >
      <div
        ref={textRef}
        className="absolute whitespace-nowrap font-medium flex gap-2"
        style={{ willChange: "transform" }}
      >
        <img src="https://ik.imagekit.io/238r9emirjj/certified%20app/Recommend.svg?updatedAt=1741343168361" alt="Recommend icon" />
        {text}
      </div>
    </div>
  );
};

const SingleItemTicker = ({ text }) => {
  return (
    <div className="w-full">
      <div
        className="h-[36px] flex items-center justify-center text-white"
        style={{
          background:
            "linear-gradient(135deg, #2E5CA4 0%, #244779 50%, #22426D 100%)",
        }}
      >
        <Ticker text={text} speed={0.8} className="h-full" />
      </div>
    </div>
  );
};

export default SingleItemTicker;